import { Component, OnInit, Input, OnChanges, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { Table, Pagination, TableData, TableDataTypes } from 'src/app/shared/types';
import { HostDetailsService } from '../host-details.service';
import { LtrService } from 'src/app/ltr.service';

export interface ReferralTrackingResponse {
  referrerProfileId: string;
  lifetimeEarnings: number;
  potentialEarnings: number;
  pendingReferrerAmount: number;
  referrerRewards: number;
  pendingReferredAmount: number;
  referredRewards: number;
  totalPendingRewards: number;
  totalReferralRewards: number;
  referredUsers: Array<{
    profileId: string;
    name: string;
    mobileNumber: string;
    role: string;
    ridesCompleted: number;
    ridesRequiredToComplete: number;
    amountEarned: number;
    canNudge: boolean;
    orderId: string;
    codeAppliedDate: string;
    referralRewardAchieved: string;
  }>;
  pagination: { limit: number; offset: number; total: number };
}

@Component({
  selector: 'app-host-referral-rewards',
  templateUrl: './host-referral-rewards.component.html',
  styleUrls: ['./host-referral-rewards.component.css']
})
export class HostReferralRewardsComponent implements OnInit, OnChanges {
  @Input() profileId: string;
  @Input() hostDetailsToggleableHeaders: string[];
  @Output() toggleHeaders: EventEmitter<string> = new EventEmitter();

  referralTable: Table;
  dataLoaded = false;
  errorMessage: string = null;
  lifetimeEarnings = 0;
  potentialEarnings = 0;
  pendingReferrerAmount = 0;
  referrerRewards = 0;
  pendingReferredAmount = 0;
  referredRewards = 0;
  totalPendingRewards = 0;
  totalReferralRewards = 0;
  perPage = 20;
  currentOffset = 0;
  totalCount = 0;
  private ltrCache: { [key: string]: string } = {};
  private referralLoadVersion = 0;

  constructor(private hostDetailsService: HostDetailsService, private ltrService: LtrService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.profileId && changes.profileId.currentValue) {
      this.profileId = changes.profileId.currentValue;
      if (this.profileId) {
        this.currentOffset = 0;
        this.initializeTable();
        this.loadReferralTracking();
      }
    }
  }

  ngOnInit() {
    if (this.profileId) {
      this.initializeTable();
      this.loadReferralTracking();
    }
  }

  changeInHeader(header: string) {
    this.toggleHeaders.emit(header);
  }

  initializeTable(pageNumber = 0) {
    const headersList = this.hostDetailsToggleableHeaders || [];
    this.referralTable = new Table({
      tableHeader: '',
      toggleableHeaders: headersList,
      selectedHeader: headersList.indexOf('Referral Rewards'),
      headers: {
        phoneNumber: 'Phone number',
        name: 'Name of the User',
        orderId: 'Order ID',
        ridesCompleted: 'Rides completed',
        ltr: 'LTR',
        rewardsEarned: 'Rewards Earned',
        role: 'Role',
        codeAppliedDate: 'Code Applied Date',
        referralRewardAchieved: 'Referral Reward Achieved',
      },
      pagination: new Pagination(pageNumber, this.perPage),
      defaultError: 'No referral data found',
      data: [],
      config: { refresh: true },
      onRefresh: () => {
        this.loadReferralTracking();
      }
    });
  }

  loadReferralTracking() {
    if (!this.profileId) {
      this.dataLoaded = true;
      return;
    }
    this.dataLoaded = false;
    this.errorMessage = null;
    const loadVersion = ++this.referralLoadVersion;
    this.hostDetailsService.getReferralTracking(this.profileId, this.perPage, this.currentOffset).subscribe(
      (res: ReferralTrackingResponse) => {
        if (loadVersion !== this.referralLoadVersion) {
          return;
        }
        this.dataLoaded = true;
        this.lifetimeEarnings = res.lifetimeEarnings != null ? res.lifetimeEarnings : 0;
        this.potentialEarnings = res.potentialEarnings != null ? res.potentialEarnings : 0;
        this.pendingReferrerAmount = res.pendingReferrerAmount != null ? res.pendingReferrerAmount : 0;
        this.referrerRewards = res.referrerRewards != null ? res.referrerRewards : 0;
        this.pendingReferredAmount = res.pendingReferredAmount != null ? res.pendingReferredAmount : 0;
        this.referredRewards = res.referredRewards != null ? res.referredRewards : 0;
        this.totalPendingRewards = res.totalPendingRewards != null
          ? res.totalPendingRewards
          : (this.pendingReferrerAmount || this.pendingReferredAmount)
            ? (this.pendingReferrerAmount + this.pendingReferredAmount)
            : this.potentialEarnings;
        this.totalReferralRewards = res.totalReferralRewards != null
          ? res.totalReferralRewards
          : (this.referrerRewards || this.referredRewards)
            ? (this.referrerRewards + this.referredRewards)
            : this.lifetimeEarnings;
        this.totalCount = (res.pagination && res.pagination.total != null) ? res.pagination.total : 0;
        const createTableData = (value: string | number, className?: string, onClick?: () => void) =>
          new TableData({
            data: String(value != null ? value : ''),
            type: TableDataTypes.DATA,
            className: onClick && className ? className + ' cursor-pointer' : (className || ''),
            onClick: onClick || (() => {})
          });
        const hasMobileAndName = (user) => {
          const mobile = user.mobileNumber != null ? String(user.mobileNumber).trim() : '';
          const name = user.name != null ? String(user.name).trim() : '';
          return mobile !== '' && name !== '';
        };
        const referredUsers = (res.referredUsers || []).filter(hasMobileAndName);
        this.referralTable.data = referredUsers
          .map((user, index) => {
            const roleDisplay = user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : '';
            const ridesCompleted = user.ridesCompleted != null ? user.ridesCompleted : 0;
            const ltrDisplay = ridesCompleted === 0 ? '0' : 'Loading...';
            return {
              phoneNumber: createTableData(user.mobileNumber || '', 'link-style'),
              name: createTableData(user.name || '', 'link-style'),
              role: createTableData(roleDisplay),
              ridesCompleted: createTableData(ridesCompleted),
              ltr: createTableData(ltrDisplay),
              rewardsEarned: createTableData(this.formatCurrency(user.amountEarned != null ? user.amountEarned : 0)),
              orderId: createTableData(user.orderId || '', 'link-style'),
              codeAppliedDate: createTableData(user.codeAppliedDate ? this.formatDate(user.codeAppliedDate) : ''),
              referralRewardAchieved: createTableData(user.referralRewardAchieved ? this.formatDate(user.referralRewardAchieved) : ''),
            };
          });
        referredUsers.forEach((user, index) => this.loadLtrForUser(user, index, loadVersion));
        if (this.referralTable.pagination) {
          this.referralTable.pagination.count = this.totalCount;
        }
      },
      (err) => {
        if (loadVersion !== this.referralLoadVersion) {
          return;
        }
        this.dataLoaded = true;
        this.errorMessage = (err && err.error && err.error.message) ? err.error.message : 'Unable to fetch referral rewards.';
        this.referralTable.data = [];
        this.referralTable.defaultError = this.errorMessage;
      }
    );
  }

  formatCurrency(amount: number): string {
    const value = amount != null ? amount : 0;
    return '₹' + value.toLocaleString('en-IN');
  }

  formatDate(dateStr: string): string {
    if (!dateStr) { return ''; }
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? dateStr : date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  }

  private loadLtrForUser(user: any, rowIndex: number, loadVersion: number) {
    const ridesCompleted = user.ridesCompleted != null ? user.ridesCompleted : 0;
    if (!this.referralTable || !this.referralTable.data || !this.referralTable.data[rowIndex]) {
      return;
    }
    if (ridesCompleted === 0) {
      this.referralTable.data[rowIndex].ltr.data = '0';
      return;
    }
    const normalizedRole = (user.role || '').toLowerCase();
    const cacheKey = `${normalizedRole}:${user.profileId || ''}`;
    if (this.ltrCache[cacheKey]) {
      this.referralTable.data[rowIndex].ltr.data = this.ltrCache[cacheKey];
      return;
    }
    if (!user.profileId || !normalizedRole) {
      this.referralTable.data[rowIndex].ltr.data = 'N/A';
      return;
    }
    this.ltrService.getLtrForRole(user.profileId, user.role).subscribe((ltr) => {
      if (loadVersion !== this.referralLoadVersion) {
        return;
      }
      const displayValue = ltr != null ? String(ltr) : 'N/A';
      this.ltrCache[cacheKey] = displayValue;
      if (this.referralTable && this.referralTable.data && this.referralTable.data[rowIndex]) {
        this.referralTable.data[rowIndex].ltr.data = displayValue;
      }
    });
  }

  onPaginationChange(event: { perPage: number; previousPageIndex: number; pageNo: number }) {
    this.perPage = event.perPage;
    this.currentOffset = event.pageNo * event.perPage;
    this.loadReferralTracking();
  }
}
